import { create } from 'zustand';
import { format } from 'date-fns';
import type { Space } from '../types';

export type ViewType = 'track' | 'planner' | 'backlog' | 'summary' | 'archive' | 'calendar' | 'recurring';
export type ThemeId = 'midnight' | 'dawn';

interface AppState {
  currentSpaceId: string | null;
  spaces: Space[];
  currentView: ViewType;
  selectedDate: string;
  selectedTaskId: string | null;
  selectedMeetingId: string | null;
  theme: ThemeId;
  highlightedTaskId: string | null;
  headerContext: { count: number; label: string } | null;
  /**
   * Whether the Track view's timeline drawer is open.
   * Session-only (not persisted). Only meaningful at viewport widths < 1200px;
   * at wider widths the timeline panel is shown inline regardless.
   */
  showTimelineDrawer: boolean;
  authToken: string | null;
  userId: string | null;
  isAuthenticated: boolean;
  setSpaceId: (id: string) => void;
  setSpaces: (spaces: Space[]) => void;
  setView: (view: ViewType) => void;
  setSelectedDate: (date: string) => void;
  setSelectedTaskId: (id: string | null) => void;
  setSelectedMeetingId: (id: string | null) => void;
  setTheme: (theme: ThemeId) => void;
  setHighlightedTaskId: (id: string | null) => void;
  setHeaderContext: (ctx: { count: number; label: string } | null) => void;
  setShowTimelineDrawer: (open: boolean) => void;
  toggleTimelineDrawer: () => void;
  setAuth: (token: string, userId: string) => void;
  clearAuth: () => void;
}

const today = () => format(new Date(), 'yyyy-MM-dd');

const savedSpaceId = localStorage.getItem('stride-space-id');
const rawSavedView = localStorage.getItem('stride-view');
if (rawSavedView === 'notes') {
  localStorage.setItem('stride-view', 'calendar');
}
const savedView = ((rawSavedView === 'notes' ? 'calendar' : rawSavedView) as ViewType) || 'track';
const rawTheme = localStorage.getItem('stride-theme');
const savedTheme: ThemeId = rawTheme === 'dawn' ? 'dawn' : 'midnight';

function applyTheme(theme: ThemeId) {
  if (theme === 'midnight') {
    delete document.documentElement.dataset.theme;
  } else {
    document.documentElement.dataset.theme = theme;
  }
}

applyTheme(savedTheme);

export const useAppStore = create<AppState>((set) => ({
  currentSpaceId: savedSpaceId,
  spaces: [],
  currentView: savedView,
  selectedDate: today(),
  selectedTaskId: null,
  selectedMeetingId: null,
  theme: savedTheme,
  highlightedTaskId: null,
  headerContext: null,
  showTimelineDrawer: false,
  authToken: null,
  userId: null,
  isAuthenticated: false,
  setSpaceId: (id) => {
    localStorage.setItem('stride-space-id', id);
    set({ currentSpaceId: id });
  },
  setSpaces: (spaces) => {
    set({ spaces });
    // If no saved space or saved space doesn't exist in list, pick first
    const current = localStorage.getItem('stride-space-id');
    if (!current || !spaces.find(s => s.id === current)) {
      if (spaces.length > 0) {
        localStorage.setItem('stride-space-id', spaces[0].id);
        set({ currentSpaceId: spaces[0].id });
      }
    }
  },
  setView: (view) => {
    localStorage.setItem('stride-view', view);
    set({ currentView: view });
  },
  setSelectedDate: (date) => set({ selectedDate: date }),
  setSelectedTaskId: (id) => set({ selectedTaskId: id }),
  setSelectedMeetingId: (id) => set({ selectedMeetingId: id }),
  setTheme: (theme) => {
    localStorage.setItem('stride-theme', theme);
    applyTheme(theme);
    set({ theme });
  },
  setHighlightedTaskId: (id) => set({ highlightedTaskId: id }),
  setHeaderContext: (ctx) => set({ headerContext: ctx }),
  setShowTimelineDrawer: (open) => set({ showTimelineDrawer: open }),
  toggleTimelineDrawer: () => set((s) => ({ showTimelineDrawer: !s.showTimelineDrawer })),
  setAuth: (token, userId) => set({ authToken: token, userId, isAuthenticated: true }),
  clearAuth: () => set({ authToken: null, userId: null, isAuthenticated: false }),
}));
